import { Widget } from '@/components/widget';
import { ObjectsTable } from '@/components/objects-table';
import { useEmitter } from '@/hooks/use-emitter';
import { useTabId } from '@/hooks/use-tab-id';
import { useScreenId } from '@/hooks/use-screen-id';
import { useUserContainers } from '@/hooks/user/use-user-containers';
import { useState } from 'react';
import { Modal } from '@/components/modal';
import { useDeleteContainer } from '@/features/containers/hooks/use-delete-container';
import { useUserConfig } from '@/hooks/user/use-user-config';

interface ContainerListWidgetProps {
  id: string;
  hideable?: boolean;
}

export function ContainerListWidget({
  id,
  hideable = false,
}: ContainerListWidgetProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [containerUuid, setContainerUuid] = useState<string | null>(null);
  const tabId = useTabId();
  const screenId = useScreenId();
  const emit = useEmitter({
    id,
    listenersPrefix: `${tabId}/${screenId}`,
  });
  const { data, isLoading } = useUserContainers();
  const { refetch: refetchUserConfig } = useUserConfig();
  const { mutate: deleteContainer, isLoading: isDeleteLoading } =
    useDeleteContainer();

  function handleDelete(uuid: string) {
    setShowDeleteModal(true);
    setContainerUuid(uuid);
  }

  function handleEdit(uuid: string) {
    emit({
      type: 'ObjectCollection',
      data: [{ uuid }],
    });
  }

  function handleDeleteCancelClick() {
    setShowDeleteModal(false);
    setContainerUuid(null);
  }

  function handleDeleteConfirmClick() {
    if (!containerUuid) {
      return;
    }

    deleteContainer(
      { containerUuid },
      {
        onSuccess: () => {
          refetchUserConfig();
          setContainerUuid(null);
          setShowDeleteModal(false);
          emit({
            type: 'ObjectCollection',
            data: [],
          });
        },
      }
    );
  }

  function handleDragData(uuid: string, label?: string) {
    return JSON.stringify({
      type: 'object/insert',
      value: [{ uuid, type: 'container', label }],
    });
  }

  const transformedContainers = data?.containers?.map((container) => ({
    uuid: container.value,
    name: container.label,
  }));

  return (
    <>
      <Widget id={id} title="Containers" hideable={hideable}>
        <ObjectsTable
          data={transformedContainers || []}
          isLoading={isLoading}
          onDelete={handleDelete}
          onEdit={handleEdit}
          setDragData={handleDragData}
        />
        {showDeleteModal && (
          <Modal
            onCancel={handleDeleteCancelClick}
            onSubmit={handleDeleteConfirmClick}
            loading={isDeleteLoading}
            title="Delete container"
            message="Are you sure you want to delete this container? This action cannot be undone."
            actionName="Delete"
          />
        )}
      </Widget>
    </>
  );
}

export default ContainerListWidget;
